import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { Button } from '../ui/Button';

interface RawJSONViewerProps {
  data: any;
  title?: string;
}

export const RawJSONViewer: React.FC<RawJSONViewerProps> = ({ data, title = 'JSON Payload' }) => {
  const [copied, setCopied] = useState(false);
  const formattedJSON = JSON.stringify(data, null, 2);

  const handleCopy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(formattedJSON);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = formattedJSON;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        textarea.remove();
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy JSON:', err);
    }
  };

  const renderHighlightedJSON = (jsonString: string) => {
    const lines = jsonString.split('\n');
    return lines.map((line, idx) => {
      const match = line.match(/^(\s*)(".*?"):(.*)$/);
      if (match) {
        const [, indent, key, val] = match;
        return (
          <div key={idx} className="leading-5">
            <span className="text-zinc-600 select-none mr-3 text-right inline-block w-6">
              {idx + 1}
            </span>
            <span>{indent}</span>
            <span className="text-sky-400">{key}</span>:
            <span className={getValueClass(val)}>{val}</span>
          </div>
        );
      }
      return (
        <div key={idx} className="leading-5">
          <span className="text-zinc-600 select-none mr-3 text-right inline-block w-6">
            {idx + 1}
          </span>
          <span className="text-zinc-400">{line}</span>
        </div>
      );
    });
  };

  const getValueClass = (val: string) => {
    const trimmed = val.trim().replace(/,$/, '');
    if (trimmed.startsWith('"')) return ' text-emerald-400';
    if (!isNaN(Number(trimmed))) return ' text-amber-400';
    if (trimmed === 'true' || trimmed === 'false') return ' text-purple-400';
    if (trimmed === 'null') return ' text-zinc-500 italic';
    return ' text-zinc-200';
  };

  return (
    <div className="flex flex-col bg-cf-bg border border-cf-border rounded-md overflow-hidden text-xs">
      <div className="flex items-center justify-between px-3 py-2 bg-cf-surface border-b border-cf-border">
        <span className="text-cf-textSecondary text-xs font-sans font-medium">{title}</span>
        <Button
          type="button"
          variant="outline"
          size="xs"
          onClick={handleCopy}
          icon={copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
          title="Copy raw JSON to clipboard"
        >
          <span className={copied ? 'text-emerald-400 font-sans text-xs' : 'font-sans text-xs'}>
            {copied ? 'Copied!' : 'Copy JSON'}
          </span>
        </Button>
      </div>
      <div className="p-3 overflow-x-auto max-h-[420px] select-text">
        <pre className="font-mono text-xs">{renderHighlightedJSON(formattedJSON)}</pre>
      </div>
    </div>
  );
};
